if (!customElements.get("custom-tabs")) {
    class CustomTabs extends HTMLElement {
        /** @type {MutationObserver|null} */
        #loyaltyObserver = null;

        connectedCallback() {
            this.#bindButtons();
            this.#observeLoyaltyPage();
        }

        disconnectedCallback() {
            this.#loyaltyObserver?.disconnect();
        }

        #getLoyaltyPage() {
            return this.closest("loyalty-page");
        }

        #getButtons() {
            return Array.from(this.querySelectorAll("[data-tab-sections]"));
        }

        /**
         * Resolves all panel elements for a given button from the loyalty page.
         * @param {HTMLElement} button
         * @returns {HTMLElement[]}
         */
        #getPanels(button) {
            const loyaltyPage = this.#getLoyaltyPage();
            if (!loyaltyPage) return [];

            const selectors = (button.dataset.tabSections ?? "")
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean);

            /** @type {HTMLElement[]} */
            const panels = [];

            for (const selector of selectors) {
                const el = loyaltyPage.querySelector(selector);
                if (el instanceof HTMLElement) panels.push(el);
            }

            return panels;
        }

        /**
         * Activates one tab button and shows its panels; deactivates all others.
         * @param {HTMLElement} activeButton
         */
        #activateTab(activeButton) {
            for (const button of this.#getButtons()) {
                const isActive = button === activeButton;

                button.setAttribute("aria-selected", String(isActive));
                button.toggleAttribute("selected", isActive);

                for (const panel of this.#getPanels(button)) {
                    panel.hidden = !isActive;
                }
            }
        }

        #bindButtons() {
            for (const button of this.#getButtons()) {
                button.setAttribute("role", "tab");
                button.setAttribute("aria-selected", button.hasAttribute("selected") ? "true" : "false");

                button.addEventListener("click", () => {
                    this.#activateTab(button);
                });
            }
        }

        /**
         * Once the loyalty page injects Appstle content, hide non-active panels
         * and activate the initially selected tab.
         * @param {HTMLElement} loyaltyPage
         */
        #applyInitialState(loyaltyPage) {
            const buttons = this.#getButtons();
            if (!buttons.length) return;

            const selected = buttons.find((b) => b.hasAttribute("selected")) ?? buttons[0];

            for (const button of buttons) {
                const isActive = button === selected;

                button.setAttribute("aria-selected", String(isActive));
                button.toggleAttribute("selected", isActive);

                for (const panel of this.#getPanels(button)) {
                    panel.hidden = !isActive;
                }
            }
        }

        #observeLoyaltyPage() {
            const loyaltyPage = this.#getLoyaltyPage();
            if (!loyaltyPage) return;

            const appstleContainer = loyaltyPage.querySelector("#appstleLoyaltyPage");
            if (!(appstleContainer instanceof HTMLElement)) return;

            this.#loyaltyObserver = new MutationObserver(() => {
                this.#applyInitialState(loyaltyPage);
            });

            this.#loyaltyObserver.observe(appstleContainer, { childList: true, subtree: false });
        }
    }

    customElements.define("custom-tabs", CustomTabs);
}
